import React, { useEffect } from 'react';

import {
  useCommentFormState,
  useCommentFormActions
} from './CommentFormContext';
import CommentRichTextEditor from './CommentRichTextEditor';
import CommentFormattingControls from './CommentFormattingControls';
import Form from '../../Shared/UI/Form/components/Form';
import Field from '../../Shared/UI/Field/components/Field';
import FormActions from '../../Shared/UI/FormActions/components/FormActions';
import Button from '../../Shared/UI/Button/components/Button';
import ResponsiveContainer from '../../Shared/UI/ResponsiveContainer/components/ResponsiveContainer';

import { setCursorPositionInDOM } from '../../RichTextEditor';
import { createComment } from '../../Feed/components/restApi';

import editorStyles from './CommentRichTextEditor.module.scss';

const CommentForm = ({ parentId, parentType, handleCloseModal }) => {
  const state = useCommentFormState();
  const {
    initializeState,
    setEditorState,
    onResponse,
    onError,
  } = useCommentFormActions();

  useEffect(() => {
    initializeState();
  }, []);

  const documentManager = state?.commentForm?.documentManager;

  const editorState = documentManager ? { documentManager } : null;

  useEffect(() => {
    if (documentManager) {
      setCursorPositionInDOM({
        editableContentId: editorStyles.commentEditorContainer,
        isibuteDocument: documentManager.getCurrentDocument(),
      });
    }
  }, [documentManager?.historyIndex]);

  const getPlainText = () => {
    if (!documentManager) return '';
    return documentManager.getCurrentDocument().plainText();
  };

  const getPlainTextLength = () => getPlainText().length;

  const handleSubmit = () => {
    const plainTextLength = getPlainTextLength();
    if (plainTextLength < 1 || plainTextLength > 1500) {
      return null;
    }

    createComment({
      onResponse,
      onError,
      body: {
        authenticityToken: document.querySelector("meta[name='csrf-token']").getAttribute("content"),
        plainTextMessage: getPlainText(),
        documentComponents: documentManager.getCurrentDocument().asJSON().documentComponents,
        parentId: parentId,
        parentType: parentType,
      },
    });
  };

  const isWithinCharacterLimit = () => getPlainTextLength() <= 1500;

  const getMessageHint = () => {
    return `${getPlainTextLength()}/1500 characters`;
  };

  const getMessageError = () => {
    const count = getPlainTextLength();

    if (!isWithinCharacterLimit()) {
      return `This comment has ${count} characters, which exceeds the 1500 character limit. It will not successfully submit until you decrease the character count.`;
    }

    return undefined;
  };

  if (!editorState) {
    return null;
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Field label="Message" id="message" hint={getMessageHint()} error={getMessageError()}>
        <div>
          <CommentFormattingControls
            contentEditable={true}
            setState={setEditorState}
            activeStyleTypes={state?.commentForm?.activeStyleTypes}
            activeFormat={state?.commentForm?.activeFormat}
            activeHref={state?.commentForm?.activeHref}
            editableContentId={editorStyles.commentEditorContainer}
          />
          <CommentRichTextEditor
            state={editorState}
            setState={setEditorState}
            contentEditable={true}
          />
        </div>
      </Field>

      <ResponsiveContainer name="form-actions">
        <FormActions>
          <Button
            variant="secondary"
            onClick={() => {
              handleCloseModal();
            }}
          >
            Cancel
          </Button>
          <Button type="submit">
            Create
          </Button>
        </FormActions>
      </ResponsiveContainer>
    </Form>
  );
};

export default CommentForm;
